<template>
  <ul :class="menuClass">
    <!-- Client Navigation -->
    <template v-if="isClient">
      <NavMenuItem
        :to="`/client/${clientId}`"
        :icon="InboxIcon"
        label="Orders"
        :activeRouteNames="['Client_Home', 'Client_Order']"
      />
      <NavMenuItem
        :to="`/client/${clientId}/order`"
        :icon="PlusIcon"
        label="New order"
        :activeRouteNames="['Client_New_Order']"
        extraClass="mt-4"
      />
    </template>

    <!-- Provider Navigation -->
    <template v-if="isProvider">
      <NavMenuItem
        v-if="canAccessOrdersClientsProducts"
        :to="`/provider/${providerId}/inbox`"
        :icon="InboxIcon"
        label="Inbox"
        :activeRouteNames="['Provider_Home']"
      />
      <NavMenuItem
        v-if="canAccessOrdersClientsProducts"
        :to="`/provider/${providerId}/orders`"
        :icon="CircleStackIcon"
        label="Orders"
        :activeRouteNames="[
          'Provider_Orders',
          'Provider_Order',
          'Provider_New_Order',
        ]"
      />
      <NavMenuItem
        v-if="canAccessDeliveries"
        :to="`/provider/${providerId}/deliveries`"
        :icon="TruckIcon"
        label="Deliveries"
        :activeRouteNames="['Provider_Deliveries', 'Provider_Delivery']"
      />
      <NavMenuItem
        v-if="canAccessOrdersClientsProducts"
        :to="`/provider/${providerId}/products`"
        :icon="ShoppingCartIcon"
        label="Products"
        :activeRouteNames="['Provider_Products', 'Provider_Product']"
      />
      <NavMenuItem
        v-if="canAccessOrdersClientsProducts"
        :to="`/provider/${providerId}/product-bundles`"
        :icon="RectangleGroupIcon"
        label="Bundles"
        :activeRouteNames="['Provider_Product_Bundles', 'Provider_Product_Bundle']"
      />
      <NavMenuItem
        v-if="canAccessOrdersClientsProducts"
        :to="`/provider/${providerId}/clients`"
        :icon="UsersIcon"
        label="Clients"
        :activeRouteNames="['Provider_Clients', 'Provider_Client']"
      />
      <NavMenuGroup
        v-if="canAccessInventory"
        :mainTo="`/provider/${providerId}/inventory`"
        :mainIcon="ArchiveBoxIcon"
        mainLabel="Inventory"
        :items="inventoryItems"
      />
      <NavMenuItem
        v-if="canAccessAdmin"
        :to="`/provider/${providerId}/locations`"
        :icon="MapIcon"
        label="Locations"
        :activeRouteNames="['Provider_Locations', 'Provider_Location']"
      />
      <NavMenuItem
        v-if="canAccessAdmin"
        :to="`/provider/${providerId}/metadata`"
        :icon="CogIcon"
        label="Custom Fields"
        :activeRouteNames="['Provider_Metadata']"
      />
      <NavMenuItem
        v-if="canAccessAdmin"
        :to="`/provider/${providerId}/users`"
        :icon="UserPlusIcon"
        label="Users"
        :activeRouteNames="['Provider_Users']"
      />
      <NavMenuItem
        v-if="canAccessAdmin"
        :to="`/provider/${providerId}/notifications`"
        :icon="BellAlertIcon"
        label="Notifications"
        :activeRouteNames="[
          'Provider_Notifications',
          'Provider_Notification_Events',
        ]"
      />
      <NavMenuItem
        v-if="canAccessAdmin"
        :to="`/provider/${providerId}/settings`"
        :icon="WrenchScrewdriverIcon"
        label="Settings"
        :activeRouteNames="['Provider_Settings']"
      />
    </template>
  </ul>
</template>

<script>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import NavMenuItem from "./NavMenuItem.vue";
import NavMenuGroup from "./NavMenuGroup.vue";
import utils from "@/helpers/utils.js";
import { isAccessBucketAllowed } from "@/helpers/providerAccess.js";
import {
  InboxIcon,
  ShoppingCartIcon,
  CircleStackIcon,
  UsersIcon,
  UserPlusIcon,
  PlusIcon,
  MapIcon,
  ArchiveBoxIcon,
  BellAlertIcon,
  CogIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  RectangleGroupIcon,
} from "@heroicons/vue/24/outline";

export default {
  name: "AppNavigation",
  components: {
    NavMenuItem,
    NavMenuGroup,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const route = useRoute();
    const store = useStore();

    const isClient = computed(() => {
      return route && route.name && route.name.startsWith("Client_");
    });

    const isProvider = computed(() => {
      return route && route.name && route.name.startsWith("Provider_");
    });

    const clientId = computed(() => {
      return (route && route.params.client_id) || null;
    });

    const providerId = computed(() => {
      return (route && route.params.provider_id) || null;
    });

    const menuClass = computed(() => {
      if (props.isMobile) {
        return "menu px-2 w-80 h-full";
      }
      return isClient.value ? "menu px-2 w-56 h-full" : "pl-2 menu w-56 h-full";
    });

    const deniedPermissions = utils.useDeniedPermissions(store, providerId);

    const canAccessDeliveries = computed(() =>
      isAccessBucketAllowed(deniedPermissions.value, "deliveries")
    );

    const canAccessInventory = computed(() =>
      isAccessBucketAllowed(deniedPermissions.value, "inventory")
    );

    const canAccessOrdersClientsProducts = computed(() =>
      isAccessBucketAllowed(deniedPermissions.value, "orders_clients_products")
    );

    const canAccessAdmin = computed(() =>
      isAccessBucketAllowed(deniedPermissions.value, "admin")
    );

    const inventoryItems = computed(() => {
      if (!providerId.value) return [];
      const items = [
        {
          to: `/provider/${providerId.value}/inventory`,
          label: "Stock",
          activeRouteNames: ["Provider_Inventory"],
        },
        {
          to: `/provider/${providerId.value}/transactions`,
          label: "Movements",
          activeRouteNames: [
            "Provider_Transactions",
            "Provider_Transaction",
            "Provider_New_Transaction",
          ],
        },
      ];

      return items;
    });

    return {
      isClient,
      isProvider,
      clientId,
      providerId,
      menuClass,
      inventoryItems,
      canAccessDeliveries,
      canAccessInventory,
      canAccessOrdersClientsProducts,
      canAccessAdmin,
      InboxIcon,
      ShoppingCartIcon,
      CircleStackIcon,
      UsersIcon,
      PlusIcon,
      UserPlusIcon,
      MapIcon,
      ArchiveBoxIcon,
      BellAlertIcon,
      CogIcon,
      TruckIcon,
      WrenchScrewdriverIcon,
      RectangleGroupIcon,
    };
  },
};
</script>
